import React, { forwardRef, useEffect, useState } from "react";
import { polyfill as scrollPolyfill } from "seamless-scroll-polyfill";
import { Box, Typography, useTheme } from "@mui/material";

import ScrollableWrapper from "../ScrollableWrapper";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import {
  InteractiveButtonsRoot,
  CustomTab,
  CustomTabs,
  TabContentWrapper,
  TabContent,
  OptionList,
  OptionButton,
  OptionButtonImg,
  ToggleMenuBtn,
  CinematicControls,
  CinematicButton,
} from "./InteractiveButtons.styles";
import { useRouteParams } from "../../context/RouteParamsContext";

const InteractiveButtons = forwardRef((props, ref) => {
  const {
    emitUIInteraction,
    actionsList,
    className,
    applicationResponse,
    loading,
  } = props;
  const theme = useTheme();

  const { projectName } = useRouteParams();

  useEffect(() => {
    scrollPolyfill({ duration: 90 });
  }, []);

  const defaultActiveOptions = [
    '{"bm":"s"}',
    '{"bc":"bla"}',
    '{"bd":"d1"}',
    '{"bdc":"bl"}',
    '{"mv":"se_v"}',
    '{"mv":"ca_h"}',
    '{"mv":"pe_v"}',
    '{"mv":"int1_v"}',
    '{"env":"e1"}',
    '{"ap":"pl"}',
  ];

  const [firstLevelOptionIndex, setFirstLevelOptionIndex] = useState(() => {
    const saved = localStorage.getItem("firstLevelOptionIndex");
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [mainActions, setMainActions] = useState([]);
  const [cinematicButtons, setCinematicButtons] = useState();
  const [toggleInterface, setToggleInterface] = useState(true);
  const [showPlay, setShowPlay] = useState(true);
  const [activeOptions, setActiveOptions] = useState(() => {
    const stored = localStorage.getItem("activeOptions");
    return stored ? JSON.parse(stored) : defaultActiveOptions;
  });

  const playButton = cinematicButtons?.subActions.find(
    (val) => val.name === "Play"
  );
  const pauseButton = cinematicButtons?.subActions.find(
    (val) => val.name === "Pause"
  );

  const multiLevelActions = () => {
    const newActionsArray = [];
    actionsList?.forEach((item) => {
      if (item.name === "Cinematic") setCinematicButtons(item);
      else newActionsArray.push(item);
    });
    setMainActions(newActionsArray);
  };

  const handleChangeFirstLevel = (newValue) => {
    setFirstLevelOptionIndex(newValue);
    localStorage.setItem("firstLevelOptionIndex", newValue);
  };

  const handleChangeSecondLevel = (newValue) => {
    // setSecondLevelOptionIndex(newValue);
  };

  useEffect(() => {
    multiLevelActions();
  }, []);

  const clickOnAction = (descriptor) => {
    emitUIInteraction(descriptor);
  };

  const deleteCurrentActiveOption = (descriptor) => {
    const key = Object.keys(descriptor)[0];
    const newValue = descriptor[key];
    if (!key || !newValue) return;

    const newEntry = JSON.stringify({ [key]: newValue });

    const newPrefix = newValue.includes("_") ? newValue.split("_")[0] : null;

    const newActiveOptionsArray = activeOptions.filter((item) => {
      try {
        const parsed = JSON.parse(item);
        const parsedKey = Object.keys(parsed)[0];
        const parsedValue = parsed[parsedKey];

        // 🔁 SPECIAL CASE 1: reset_cam → remove int_cam and reset_cam
        if (key === "reset_cam") {
          return parsedKey !== "reset_cam" && parsedKey !== "int_cam";
        }

        // 🔁 SPECIAL CASE 2: int_cam → remove int_cam and reset_cam
        if (key === "int_cam") {
          return parsedKey !== "reset_cam" && parsedKey !== "int_cam";
        }
        // Keep if it's a different key
        if (parsedKey !== key) return true;

        // Same key – need to check value

        // Case 1: values with underscores – check prefix
        if (newPrefix && parsedValue.includes("_")) {
          const parsedPrefix = parsedValue.split("_")[0];
          return parsedPrefix !== newPrefix;
        }

        // Case 2: no underscore – remove if different value
        return parsedValue === newValue;
      } catch {
        return true;
      }
    });

    if (!newActiveOptionsArray.includes(newEntry)) {
      newActiveOptionsArray.push(newEntry);
    }

    setActiveOptions(newActiveOptionsArray);
    localStorage.setItem(
      "activeOptions",
      JSON.stringify(newActiveOptionsArray)
    );
  };

  const updateActiveOptionsArray = (response) => {
    if (response?.length > 0 && !response.includes("CreateScreenshot")) {
      deleteCurrentActiveOption(JSON.parse(response), response);
    }
  };

  const checkIfOptionIsActive = (option) => {
    if (!option || typeof option !== "object") return false;

    const [key, value] = Object.entries(option)[0]; // Only the first entry
    const formatted = JSON.stringify({ [key]: value });

    return activeOptions.includes(formatted);
  };

  useEffect(() => {
    if (!applicationResponse?.includes("Console")) {
      const responseFormatted = applicationResponse?.replace(/\s/g, "");
      updateActiveOptionsArray(responseFormatted);
    }

    const regexPlay = /"cin"\s*:\s*"pl"/;
    const regexPause = /"cin"\s*:\s*"pa"/;
    const regexStop = /"cin"\s*:\s*"st"/;
    if (regexPlay.test(applicationResponse)) setShowPlay(false);
    if (regexPause.test(applicationResponse)) setShowPlay(true);
    if (regexStop.test(applicationResponse)) setShowPlay(true);

    const cinematicMatch = applicationResponse?.match(
      /"cinematic_scrub":\s*"([0-9.]+)"/
    );
    if (cinematicMatch) {
      // const scrub = parseFloat(cinematicMatch[1]) * 100;
      // setCinematicTime(parseFloat(scrub.toFixed(2)));
    }
  }, [applicationResponse]);

  const takeScreenshot = mainActions.find(
    (item) => item.name === "Take a photo"
  );

  return (
    !loading && (
      <InteractiveButtonsRoot className={className} show={toggleInterface}>
        <ToggleMenuBtn
          onClick={() => setToggleInterface(!toggleInterface)}
          show={toggleInterface}
        >
          <DoubleArrowIcon sx={{ transform: "rotate(180deg)" }} />
          <Typography
            sx={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: "1.6rem",
              marginLeft: "1rem",
            }}
          >
            {toggleInterface ? "Hide" : "Show Options"}
          </Typography>
        </ToggleMenuBtn>
        <Box
          sx={{
            width: "100%",
          }}
        >
          <CustomTabs>
            {mainActions.map((item, index) => {
              const { name, action, model } = item;
              if (action?.type === "activate") return undefined;
              if (
                (name === "Options" || name === "Options Interior") &&
                projectName === "electra"
              )
                return undefined;
              if (name === "Options Interior" && projectName === "airbus")
                return undefined;
              if (name === "Camera views" && projectName === "electra")
                return undefined;
              if (projectName === "airbus" && model === "electra")
                return undefined;
              if (name === "Take a photo") return undefined;
              return (
                <React.Fragment key={`${index + 1} ${name}`}>
                  <CustomTab
                    value={index}
                    label={name}
                    active={firstLevelOptionIndex === index}
                    onClick={() => {
                      handleChangeFirstLevel(index);
                      handleChangeSecondLevel(0);
                      clickOnAction(action?.descriptor);
                    }}
                  />
                </React.Fragment>
              );
            })}
          </CustomTabs>
          <TabContentWrapper show={toggleInterface}>
            {mainActions.map((item, indexOption) => {
              const { subActions, name } = item;
              if (item.action?.type === "activate") return undefined;
              return (
                <TabContent
                  key={`${name}${indexOption}`}
                  role="tabpanel"
                  hidden={firstLevelOptionIndex !== indexOption}
                  id={`full-width-tabpanel-${indexOption}`}
                  aria-labelledby={`full-width-tab-${indexOption}`}
                >
                  <Box
                    className="product-options__wrapper"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      [theme.breakpoints.down("md")]: {
                        width: "100vw",
                        alignItems: "flex-start",
                        alignContent: "flex-start",
                        "@media (orientation: landscape)": {
                          alignItems: "flex-start",
                          alignContent: "flex-start",
                        },
                      },
                    }}
                  >
                    {subActions.map((itemSubActions, indexItemSubActions) => (
                      <TabContent
                        key={`${itemSubActions.name}${indexItemSubActions}`}
                        id={`full-width-tabpanel-${indexItemSubActions}`}
                        aria-labelledby={`full-width-tab-${indexItemSubActions}`}
                      >
                        <ScrollableWrapper
                          key={`${itemSubActions.name}${indexItemSubActions}`}
                        >
                          {itemSubActions?.subActions?.map(
                            (innerItem, indexInnerItem) => {
                              if (innerItem?.subActions?.length > 0) {
                                if (
                                  !projectName &&
                                  innerItem.model === "electra"
                                )
                                  return undefined;
                                if (
                                  projectName === "electra" &&
                                  innerItem.model === "airbus"
                                )
                                  return undefined;
                                return (
                                  <Box
                                    key={`${innerItem.name}${
                                      indexInnerItem + 2
                                    }`}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "flex-start",
                                      alignItems: "flex-start",
                                      position: "relative",
                                    }}
                                  >
                                    <OptionList
                                      length={innerItem?.subActions?.length}
                                    >
                                      {innerItem?.subActions.map(
                                        (itemDeepLevel, indexItemDeepLevel) => (
                                          <OptionButton
                                            sx={{
                                              margin: "0 10px 0 0",
                                            }}
                                            title={itemDeepLevel.name}
                                            key={`${itemDeepLevel.name}${indexItemDeepLevel}`}
                                            active={checkIfOptionIsActive(
                                              itemDeepLevel?.action?.descriptor
                                            )}
                                            onClick={() => {
                                              clickOnAction(
                                                itemDeepLevel?.action
                                                  ?.descriptor
                                              );
                                            }}
                                          >
                                            <OptionButtonImg
                                              active={checkIfOptionIsActive(
                                                itemDeepLevel?.action
                                                  ?.descriptor
                                              )}
                                            >
                                              <Box
                                                component="img"
                                                src={itemDeepLevel.image}
                                                alt={itemDeepLevel.name}
                                              />
                                            </OptionButtonImg>
                                            <Box component="span">
                                              {itemDeepLevel.name}
                                            </Box>
                                          </OptionButton>
                                        )
                                      )}
                                    </OptionList>
                                  </Box>
                                );
                              } else {
                                if (
                                  projectName === "airbus" &&
                                  innerItem.model === "electra"
                                )
                                  return undefined;
                                if (
                                  projectName === "electra" &&
                                  innerItem.model === "airbus"
                                )
                                  return undefined;
                                return (
                                  <OptionButton
                                    title={innerItem.name}
                                    key={`${innerItem.name}${
                                      indexInnerItem + 3
                                    }`}
                                    active={checkIfOptionIsActive(
                                      innerItem?.action?.descriptor
                                    )}
                                    onClick={() => {
                                      clickOnAction(
                                        innerItem?.action?.descriptor
                                      );
                                    }}
                                  >
                                    <OptionButtonImg
                                      active={checkIfOptionIsActive(
                                        innerItem?.action?.descriptor
                                      )}
                                    >
                                      <Box
                                        component="img"
                                        src={innerItem.image}
                                        alt={innerItem.name}
                                      />
                                    </OptionButtonImg>
                                    <Box component="span">{innerItem.name}</Box>
                                  </OptionButton>
                                );
                              }
                            }
                          )}
                        </ScrollableWrapper>
                      </TabContent>
                    ))}
                  </Box>
                </TabContent>
              );
            })}
          </TabContentWrapper>
        </Box>

        <CinematicControls show={toggleInterface}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: "2rem",
              lineHeight: 1,
              fontWeight: "500",
              textTransform: "initial",
              display: { xs: "none", md: "block" },
            }}
          >
            Cinematic mode
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: "2rem",
              padding: { xs: "1.5rem 0 0 2rem", md: "1.2rem 0 0" },
              borderTop: "1px solid rgba(0, 0, 0, 1)",
              [theme.breakpoints.down("md")]: {
                "@media (orientation: landscape)": {
                  borderTop: "none",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: "2rem",
              }}
            >
              {showPlay ? (
                <CinematicButton
                  active={checkIfOptionIsActive(playButton?.action?.descriptor)}
                  onClick={() => {
                    clickOnAction(playButton?.action?.descriptor);
                  }}
                  title={playButton?.name}
                >
                  <Box component="img" src={playButton?.image} alt="" />
                </CinematicButton>
              ) : (
                <CinematicButton
                  active={checkIfOptionIsActive(
                    pauseButton?.action?.descriptor
                  )}
                  onClick={() => {
                    clickOnAction(pauseButton?.action?.descriptor);
                  }}
                  title={pauseButton?.name}
                >
                  <Box component="img" src={pauseButton?.image} alt="" />
                </CinematicButton>
              )}
            </Box>
            {cinematicButtons &&
              cinematicButtons.subActions.map(
                (cinematicItem, cinematicItemIndex) => {
                  if (
                    cinematicItem?.name === "Stop" ||
                    cinematicItem?.name === "Reset Camera"
                  ) {
                    return (
                      <CinematicButton
                        key={`${cinematicItem.name}${cinematicItemIndex}`}
                        active={checkIfOptionIsActive(
                          cinematicItem?.action?.descriptor
                        )}
                        onClick={() => {
                          clickOnAction(cinematicItem?.action?.descriptor);
                          // if (cinematicItem?.name === "Stop")
                          //   setCinematicTime(0);
                        }}
                        title={cinematicItem?.name}
                      >
                        <Box
                          component="img"
                          src={cinematicItem?.image}
                          alt=""
                        />
                      </CinematicButton>
                    );
                  }
                }
              )}
            <CinematicButton
              onClick={() => {
                clickOnAction(takeScreenshot?.action?.descriptor);
              }}
              title={takeScreenshot?.name}
            >
              <Box component="img" src={takeScreenshot?.muiIcon} alt="" />
            </CinematicButton>
            {/* <CinematicSlider
              action={cinematicSlider?.action}
              emitUIInteraction={clickOnAction}
              timeValue={cinematicTime}
              onSlidePause={setCinematicState}
            /> */}
          </Box>
        </CinematicControls>
      </InteractiveButtonsRoot>
    )
  );
});

InteractiveButtons.defaultProps = {
  open: false,
};

export { InteractiveButtons };
export default InteractiveButtons;
